import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import Loader from "../../../components/Common/Loader";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import { commonService } from "../../../services/commonService";

interface HeaderField {
    id: number;
    header_name: string;
}

const CreateRecordPage: React.FC = () => {
    const navigate = useNavigate();

    const [headers, setHeaders] = useState<HeaderField[]>([]);
    const [formData, setFormData] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(null);

    const [contactCountId, setContactCountId] = useState<number | null>(null);

    useEffect(() => {
        fetchHeaders();
    }, []);

    const fetchHeaders = async () => {
        try {
            setLoading(true);
            const response = await commonService.cdget();

            const activeHeaders =
                (response?.data || [])
                    .filter((item: any) => !item.is_deleted)
                    .reverse()
                    .map((item: any) => ({
                        id: item.id,
                        header_name: item.header_name
                    }));

            setHeaders(activeHeaders);

            const initialData: Record<number, string> = {};

            activeHeaders.forEach((h: any) => {
                initialData[h.id] = "";

                if (h.header_name === "Contact Counts") {
                    setContactCountId(h.id);
                }
            });

            setFormData(initialData);

        } catch (error) {
            setMessage({
                type: "error",
                text: "Failed to load form fields."
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (id: number, value: string) => {
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async () => {
        setMessage(null);

        // 🔴 Validate only Contact Counts using ID
        if (
            contactCountId &&
            (!formData[contactCountId] ||
                formData[contactCountId].trim() === "")
        ) {
            setMessage({
                type: "error",
                text: "Contact Counts is required."
            });
            return;
        }

        try {
            setSubmitting(true);

            // ✅ Send SAME structure, but keys = header_id
            await commonService.recordCreate({
                row_data: formData
            });

            setMessage({
                type: "success",
                text: "Record created successfully."
            });

            // Reset form
            const clearedData: Record<number, string> = {};
            headers.forEach(h => (clearedData[h.id] = ""));
            setFormData(clearedData);

        } catch (error: any) {
            const backendMessage =
                error?.response?.data?.message ||
                "Failed to create record.";

            setMessage({
                type: "error",
                text: backendMessage
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MainLayout>
            <div className="pc-counts-page custom-scrollbar">
                <main className="pc-counts-main">

                    <div className="pc-counts-header">
                        <button
                            onClick={() => navigate(-1)}
                            className="pc-counts-back-button"
                        >
                            <BackIcon />
                            Back
                        </button>
                    </div>

                    <div className="pc-counts-dashboard">
                        <div className="pc-counts-dashboard-header">
                            <h2 className="pc-counts-dashboard-title">
                                Create Record
                            </h2>
                        </div>

                        {loading ? (
                            <Loader text="Loading fields..." />
                        ) : (
                            <div style={{
                                background: "#fff",
                                padding: "30px",
                                borderRadius: "12px",
                                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                                maxWidth: "1000px",
                                margin: "0 auto"
                            }}>

                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                                    gap: "20px",
                                    marginBottom: "25px"
                                }}>
                                    {headers.map(header => {
                                        const isRequired =
                                            header.id === contactCountId;

                                        return (
                                            <div key={header.id}>
                                                <label style={{
                                                    display: "block",
                                                    marginBottom: "6px",
                                                    fontWeight: 600,
                                                    fontSize: "14px"
                                                }}>
                                                    {header.header_name}
                                                    {isRequired && (
                                                        <span style={{ color: "red" }}> *</span>
                                                    )}
                                                </label>

                                                <input
                                                    type="text"
                                                    value={formData[header.id] || ""}
                                                    onChange={(e) =>
                                                        handleChange(header.id, e.target.value)
                                                    }
                                                    style={{
                                                        width: "100%",
                                                        padding: "10px",
                                                        borderRadius: "8px",
                                                        border: "1px solid #dcdcdc"
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {message && (
                                    <div style={{
                                        padding: "12px",
                                        borderRadius: "8px",
                                        marginBottom: "20px",
                                        background:
                                            message.type === "success"
                                                ? "#e6f7ed"
                                                : "#fdecea",
                                        color:
                                            message.type === "success"
                                                ? "#1e7e34"
                                                : "#c62828"
                                    }}>
                                        {message.text}
                                    </div>
                                )}

                                <div style={{ textAlign: "right" }}>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        style={{
                                            background: "#61A1D4",
                                            color: "#fff",
                                            border: "none",
                                            padding: "10px 24px",
                                            borderRadius: "8px",
                                            fontWeight: 600
                                        }}
                                    >
                                        {submitting ? "Saving..." : "Create Record"}
                                    </button>
                                </div>

                            </div>
                        )}
                    </div>
                </main>
            </div>
        </MainLayout>
    );
};

export default CreateRecordPage;